import { Dispatch, FC, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../../lib/language';
import { CreateReferralPayoutRequest } from '../../../lib/models/crm/types';
import { RootState } from '../../../lib/store';
import { createReferralOwnPayoutRequest as createReferralOwnPayoutRequestCall } from '../../../lib/store/crm';
import { CRMCall } from '../../../lib/store/crm/types';
import { formatCurrency } from '../../../lib/util/DataHelpers';
import {
  isCallStatusError,
  isCallStatusPending,
  isCallStatusReady,
} from '../../../lib/util/error-handling/StatusByCallHelpers';

import { GlobalClassesEnum } from '../../../assets/scss/globalClasses/globalClasses.constants';
import { PayoutRequestStateEnum } from '../../../containers/Referrals/Referrals.types';
import Button from '../../../shared_components/Button/Button';
import ReferralPayoutModal from '../ReferralPayoutModal/ReferralPayoutModal';

import styles from './ReferralBalanceCard.module.scss';

type Props = ReduxProps;

const labelClasses = classNames(GlobalClassesEnum.CommonBody2Text, GlobalClassesEnum.CommonTextSecondary);

const ReferralBalanceCard: FC<Props> = ({
  createReferralOwnPayoutRequest,
  isPayoutFailed,
  isPayoutPending,
  isPayoutSucceeded,
  referralProfile,
  requestedPayoutAmount,
}: Props): JSX.Element => {
  const [payoutState, setPayoutState] = useState(PayoutRequestStateEnum.closed);
  const [balance] = referralProfile?.balances ?? [];
  const unpaidLabel = formatCurrency(balance?.unpaid ?? 0);
  const payoutAmountLabel = formatCurrency(requestedPayoutAmount ?? balance?.unpaid ?? 0);
  const isPayoutDisabled = !referralProfile?.can_request_payout || isPayoutPending || isPayoutSucceeded;

  const getPayoutModalState = () => {
    if (payoutState !== PayoutRequestStateEnum.submitted) return payoutState;
    if (isPayoutPending) return PayoutRequestStateEnum.confirm;

    return isPayoutFailed ? PayoutRequestStateEnum.failed : PayoutRequestStateEnum.submitted;
  };

  const handleConfirmPayout = () => {
    if (isPayoutPending) return;

    if (!referralProfile?.code) {
      setPayoutState(PayoutRequestStateEnum.failed);

      return;
    }

    createReferralOwnPayoutRequest({ referral_code: referralProfile.code });
    setPayoutState(PayoutRequestStateEnum.submitted);
  };

  return (
    <>
      <div className={styles.card}>
        <div className={GlobalClassesEnum.CommonH3Bold}>{lang.referralsBalance()}</div>

        <div className={styles.rows}>
          <div className={styles.row}>
            <div className={labelClasses}>{lang.referralsBalanceUnpaid()}</div>
            <div className={GlobalClassesEnum.CommonBody2TextBold}>{unpaidLabel}</div>
          </div>
          <div className={styles.row}>
            <div className={labelClasses}>{lang.referralsBalanceRequested()}</div>
            <div className={GlobalClassesEnum.CommonBody2Text}>{formatCurrency(balance?.requested ?? 0)}</div>
          </div>
          <div className={styles.row}>
            <div className={labelClasses}>{lang.referralsBalancePaid()}</div>
            <div className={GlobalClassesEnum.CommonBody2Text}>{formatCurrency(balance?.paid ?? 0)}</div>
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            fullWidth
            biggerFontSize
            className={styles.payoutButton}
            disabled={isPayoutDisabled}
            onClick={() => setPayoutState(PayoutRequestStateEnum.confirm)}
          >
            {isPayoutDisabled ? lang.referralsRequestPayout() : lang.referralsRequestPayoutAmount(unpaidLabel)}
          </Button>
          <div
            className={classNames(
              GlobalClassesEnum.CommonCaption,
              GlobalClassesEnum.CommonTextSecondary,
              styles.minimum,
            )}
          >
            {lang.referralsMinimumPayout(formatCurrency(referralProfile?.min_payout_amount ?? 0))}
          </div>
        </div>
      </div>

      <ReferralPayoutModal
        state={getPayoutModalState()}
        amountLabel={payoutAmountLabel}
        onConfirm={handleConfirmPayout}
        onClose={() => setPayoutState(PayoutRequestStateEnum.closed)}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isPayoutFailed: isCallStatusError(CRMCall.createReferralOwnPayoutRequest, state.crm.statusByCall),
  isPayoutPending: isCallStatusPending(CRMCall.createReferralOwnPayoutRequest, state.crm.statusByCall),
  isPayoutSucceeded: isCallStatusReady(CRMCall.createReferralOwnPayoutRequest, state.crm.statusByCall),
  referralProfile: state.crm.referralProfile,
  requestedPayoutAmount: state.crm.requestedPayoutAmount,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  createReferralOwnPayoutRequest: (request: CreateReferralPayoutRequest) => (
    dispatch(createReferralOwnPayoutRequestCall(request))
  ),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ReferralBalanceCard);
